import React, { useState } from 'react';

const LABELS = {
  en: {
    title: 'Build-to-Suit',
    subtitle: 'Custom warehouse solutions tailored to your operational requirements',
    name: 'Name', email: 'Email', phone: 'Phone', company: 'Company',
    area: 'Desired area (m2)', height: 'Clear height (m)', docks: 'Loading docks',
    timeline: 'Target timeline', budget: 'Budget range', requirements: 'Special requirements',
    submit: 'Submit Request', success: 'Request submitted successfully. We will be in touch shortly.',
    error: 'Something went wrong. Please try again or contact us directly.',
    budgetOptions: ['Under $500K', '$500K - $1M', '$1M - $3M', '$3M - $5M', '$5M+', 'Flexible'],
    timelineOptions: ['3-6 months', '6-12 months', '12-18 months', '18+ months', 'Flexible'],
  },
  es: {
    title: 'Construccion a la Medida',
    subtitle: 'Soluciones de almacen personalizadas para sus necesidades operativas',
    name: 'Nombre', email: 'Correo', phone: 'Telefono', company: 'Empresa',
    area: 'Area deseada (m2)', height: 'Altura libre (m)', docks: 'Muelles de carga',
    timeline: 'Plazo objetivo', budget: 'Rango de presupuesto', requirements: 'Requerimientos especiales',
    submit: 'Enviar Solicitud', success: 'Solicitud enviada exitosamente. Nos pondremos en contacto pronto.',
    error: 'Algo salio mal. Intente de nuevo o contactenos directamente.',
    budgetOptions: ['Menos de $500K', '$500K - $1M', '$1M - $3M', '$3M - $5M', '$5M+', 'Flexible'],
    timelineOptions: ['3-6 meses', '6-12 meses', '12-18 meses', '18+ meses', 'Flexible'],
  },
  zh: {
    title: '\u5b9a\u5236\u5efa\u9020',
    subtitle: '\u6839\u636e\u60a8\u7684\u8fd0\u8425\u9700\u6c42\u5b9a\u5236\u4ed3\u5e93\u89e3\u51b3\u65b9\u6848',
    name: '\u59d3\u540d', email: '\u90ae\u7bb1', phone: '\u7535\u8bdd', company: '\u516c\u53f8',
    area: '\u671f\u671b\u9762\u79ef (m2)', height: '\u51c0\u9ad8 (m)', docks: '\u88c5\u5378\u53e3',
    timeline: '\u76ee\u6807\u65f6\u95f4', budget: '\u9884\u7b97\u8303\u56f4', requirements: '\u7279\u6b8a\u8981\u6c42',
    submit: '\u63d0\u4ea4\u8bf7\u6c42', success: '\u8bf7\u6c42\u5df2\u6210\u529f\u63d0\u4ea4\u3002\u6211\u4eec\u5c06\u5c3d\u5feb\u4e0e\u60a8\u8054\u7cfb\u3002',
    error: '\u51fa\u4e86\u70b9\u95ee\u9898\u3002\u8bf7\u91cd\u8bd5\u6216\u76f4\u63a5\u8054\u7cfb\u6211\u4eec\u3002',
    budgetOptions: ['$500K\u4ee5\u4e0b', '$500K - $1M', '$1M - $3M', '$3M - $5M', '$5M+', '\u7075\u6d3b'],
    timelineOptions: ['3-6\u4e2a\u6708', '6-12\u4e2a\u6708', '12-18\u4e2a\u6708', '18+\u4e2a\u6708', '\u7075\u6d3b'],
  },
};

const BuildToSuit = ({ language = 'en' }) => {
  const l = LABELS[language] || LABELS.en;
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '',
    desired_area_sqm: '', clear_height_m: '', loading_docks: '',
    timeline: '', budget_range: '', special_requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);
    try {
      const payload = {
        ...formData,
        desired_area_sqm: formData.desired_area_sqm ? parseFloat(formData.desired_area_sqm) : null,
        clear_height_m: formData.clear_height_m ? parseFloat(formData.clear_height_m) : null,
        loading_docks: formData.loading_docks ? parseInt(formData.loading_docks) : null,
        language,
      };
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/build-to-suit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error('Failed');
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', desired_area_sqm: '', clear_height_m: '', loading_docks: '', timeline: '', budget_range: '', special_requirements: '' });
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent";
  const labelClass = "block text-xs text-gray-500 uppercase tracking-wider mb-2";

  return (
    <section id="build-to-suit" data-testid="build-to-suit-section" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="container mx-auto px-5 sm:px-6 lg:px-8 max-w-6xl">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12 lg:items-start">
          {/* Image - Desktop left side */}
          <div className="hidden lg:block lg:col-span-5">
            <img
              src="/images/renders/He_002.jpg"
              alt="Hexaparks warehouse units"
              className="w-full h-auto rounded-sm border border-gray-200 sticky top-24"
            />
          </div>

          {/* Form */}
          <div className="lg:col-span-7 max-w-2xl mx-auto lg:mx-0">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-2 text-center lg:text-left leading-snug">
              {l.title}
            </h2>
            <p className="text-sm text-gray-500 mb-8 lg:mb-10 text-center lg:text-left">{l.subtitle}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>{l.name}</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{l.email}</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{l.phone}</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{l.company}</label>
              <input type="text" name="company" value={formData.company} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className={labelClass}>{l.area}</label>
              <input type="number" name="desired_area_sqm" value={formData.desired_area_sqm} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{l.height}</label>
              <input type="number" step="0.1" name="clear_height_m" value={formData.clear_height_m} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{l.docks}</label>
              <input type="number" name="loading_docks" value={formData.loading_docks} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>{l.timeline}</label>
              <select name="timeline" value={formData.timeline} onChange={handleChange}
                className={`${inputClass} appearance-none cursor-pointer`}>
                <option value="">-</option>
                {l.timelineOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{l.budget}</label>
              <select name="budget_range" value={formData.budget_range} onChange={handleChange}
                className={`${inputClass} appearance-none cursor-pointer`}>
                <option value="">-</option>
                {l.budgetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>{l.requirements}</label>
            <textarea name="special_requirements" value={formData.special_requirements} onChange={handleChange} rows={3}
              className={`${inputClass} resize-none`} />
          </div>

          {status === 'success' && <div className="p-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm">{l.success}</div>}
          {status === 'error' && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{l.error}</div>}

          <button type="submit" disabled={isSubmitting} data-testid="bts-submit-btn"
            className="w-full sm:w-auto px-8 py-3 bg-gray-900 text-white text-xs uppercase tracking-[0.12em] hover:bg-gray-800 transition-colors disabled:opacity-50">
            {isSubmitting ? '...' : l.submit}
          </button>
        </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildToSuit;
